import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Analytics from 'analytics-opencare';

import { useAnalyticsService } from 'services/AnalyticsService';

import { STEP_KEY, generateStepAnalytics } from '../../utils';
import {
  StepWrapper,
  ButtonBlock,
  SecondaryButton,
} from '../../styled-components';
import {
  Title,
  Subtitle,
  OptionBlock,
  PrimaryButton,
} from '../../../common/styled-components';
import { useFunnelStorage } from '../../storage/Storage';
import Multiselect from '../../../common/components/MultiSelect/MultiSelect';

import { options } from './constants';

const analytics = new Analytics();

const Criteria = ({ position, moveToNextStep }) => {
  const storage = useFunnelStorage();

  const analyticsService = useAnalyticsService();

  const savedCriteria = storage.get(STEP_KEY.CRITERIA);
  const hasSavedCriteria = Array.isArray(savedCriteria);

  const [selectedCriteria, setSelectedCriteria] = useState(
    hasSavedCriteria
      ? savedCriteria.map(save => options.find(option => option.title === save))
      : [],
  );

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_criteria',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });
  }, []);

  const change = value => {
    const previouslySelected = selectedCriteria.find(
      criteria => criteria.title === value,
    );

    if (previouslySelected) {
      const newGoals = selectedCriteria.filter(goal => goal.title !== value);

      setSelectedCriteria(newGoals);
    } else {
      setSelectedCriteria([
        ...selectedCriteria,
        {
          title: value,
        },
      ]);
    }
  };

  const complete = e => {
    e.preventDefault();

    const indexes = selectedCriteria.map(
      criteria =>
        options.findIndex(option => option.title === criteria.title) + 1,
    );

    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_criteria',
      name: 'wizard',
      section: 'criteria',
      position: indexes.length > 0 ? indexes : [options.length + 1],
      ...generateStepAnalytics(position, storage, {
        response:
          selectedCriteria.length > 0
            ? selectedCriteria.map(goal => goal.title).join(',')
            : 'None of the above',
      }),
    });

    storage.save(
      STEP_KEY.CRITERIA,
      selectedCriteria.map(goal => goal.title),
      true,
    );
    moveToNextStep();
  };

  return (
    <div>
      <Title id="title">
        The most important factors in choosing a dentist are...
      </Title>
      <Subtitle>Select all that apply</Subtitle>
      <StepWrapper>
        <form onSubmit={complete}>
          <OptionBlock row={3}>
            {options.map(option => (
              <Multiselect
                key={option.id}
                id={option.id}
                name="criteria"
                title={option.title}
                onChange={change}
                Icon={option.icon}
                describedBy="title"
                initialSelected={
                  selectedCriteria.findIndex(
                    criteria => criteria.id === option.id,
                  ) > -1
                }
              />
            ))}
          </OptionBlock>
          <ButtonBlock>
            {selectedCriteria.length === 0
              ? (
                <SecondaryButton
                  data-testid="skip"
                  type="submit"
                  selected={hasSavedCriteria && savedCriteria.length === 0}
                >
                  No preference
                </SecondaryButton>
              )
              : (
                <PrimaryButton data-testid="continue" type="submit">
                  Continue
                </PrimaryButton>
              )
            }
          </ButtonBlock>
        </form>
      </StepWrapper>
    </div>
  );
};

Criteria.defaultProps = {
  position: '',
};

Criteria.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
};

export default Criteria;
